import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'

import { Loading } from '@shared/components/Loading/Loading'

import styles from './PDFDocument.module.scss'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

import classNames from 'classnames'

import { debounce } from '@utils/debounce'

interface PDFDocumentProps {
  file?: string | null
  autoHeight?: boolean
  onLoadSuccess?(): void
  setScrolledToBottom: React.Dispatch<React.SetStateAction<boolean>>
}

export const PDFDocument: React.FC<PDFDocumentProps> = (props) => {
  const { file, onLoadSuccess, setScrolledToBottom, autoHeight = false } = props

  const ref = useRef<HTMLDivElement>(null)
  const hasScrolledRef = useRef(false)
  const [numPages, setNumPages] = useState(0)
  const [isLoading, setLoading] = useState(true)
  const [width, setWidth] = useState(0)

  const checkScroll = useCallback(
    (listInnerElement: HTMLElement | null) => {
      if (listInnerElement) {
        const { scrollTop, scrollHeight, clientHeight } = listInnerElement
        const scrollAllowance = 10
        const errorAllowance = 10
        const isNearBottom = scrollTop + clientHeight + errorAllowance >= scrollHeight

        if (scrollTop > scrollAllowance) {
          hasScrolledRef.current = true
        }

        if (isNearBottom && hasScrolledRef.current) {
          setScrolledToBottom(true)
        }
      }
    },
    [setScrolledToBottom]
  )

  const onResize = useCallback((timeout: NodeJS.Timeout | undefined) => {
    return () => {
      clearTimeout(timeout)

      timeout = setTimeout(() => {
        if (ref.current) setWidth(ref.current.clientWidth)
      }, 1000)

      return timeout
    }
  }, [])

  useEffect(() => {
    const timeout: NodeJS.Timeout | undefined = undefined

    window.addEventListener('resize', onResize(timeout))

    return () => {
      window.removeEventListener('resize', onResize(timeout))
    }
  }, [onResize])

  const handleDocumentLoadSuccess = (document: { numPages: number }) => {
    const listInnerElement = ref.current
    if (listInnerElement) setWidth(listInnerElement.clientWidth)
    setNumPages(document.numPages)
    setLoading(false)
    onLoadSuccess?.()

    setTimeout(() => {
      checkScroll(listInnerElement)
    }, 200)
  }

  const handleDocumentLoadError = (error: Error) => {
    console.error('PDF Document load error:', error)
    setLoading(false)
  }

  useEffect(() => {
    const chatContainer = document.getElementById('chat-widget-container')
    const mainContainer = document.getElementById('mainContainer')
    const appFooter = document.getElementById('appFooter')

    if (chatContainer) {
      chatContainer.style.display = 'none'
    }
    if (mainContainer) {
      mainContainer.style.paddingBottom = '0'
    }
    if (appFooter) {
      appFooter.style.display = 'none'
    }

    const listInnerElement = ref.current
    hasScrolledRef.current = false

    const onScroll = debounce(() => {
      checkScroll(listInnerElement)
    }, 50)

    if (listInnerElement) {
      listInnerElement.addEventListener('scroll', onScroll)
      return () => {
        listInnerElement.removeEventListener('scroll', onScroll)
        if (chatContainer) {
          chatContainer.style.display = ''
        }
        if (mainContainer) {
          mainContainer.style.paddingBottom = ''
        }
        if (appFooter) {
          appFooter.style.display = ''
        }
      }
    }
  }, [setScrolledToBottom, ref, file])

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.autoHeight]: autoHeight,
      })}
      ref={ref}
    >
      <Document
        file={file}
        externalLinkTarget='_blank'
        loading={<Loading showLoadingIcon isLoading={isLoading} />}
        onLoadSuccess={handleDocumentLoadSuccess}
        onLoadError={handleDocumentLoadError}
      >
        {Array.from({ length: numPages }).map((_, index) => (
          <Page
            pageNumber={index + 1}
            renderTextLayer
            renderAnnotationLayer
            width={width}
            key={index}
            loading={''}
          />
        ))}
      </Document>
    </div>
  )
}
